import {
    arrayUnion,
    collection,
    doc,
    type DocumentData,
    getDocs,
    limit,
    onSnapshot,
    orderBy,
    query,
    runTransaction,
    setDoc,
    where,
} from "firebase/firestore";

import { firestore } from "@domain/api/firebase/firebase";
import type { ReleaseNote, ReleaseNoteState } from "@domain/models/ReleaseNotes";

const NOTES = "releaseNotes";
const STATE = "releaseNoteState";
/** Enough to fill the feed after a few single clears, small enough to load on every session. */
const RECENT_LIMIT = 30;

const toReleaseNote = (data: DocumentData): ReleaseNote => ({
    version: String(data.version ?? ""),
    fromVersion: data.fromVersion ? String(data.fromVersion) : null,
    order: Number(data.order ?? 0),
    date: String(data.date ?? ""),
    title: String(data.title ?? ""),
    summary: String(data.summary ?? ""),
    bodyMd: String(data.bodyMd ?? ""),
});

const toState = (data: DocumentData): ReleaseNoteState => ({
    readUpTo: Number(data.readUpTo ?? 0),
    clearedUpTo: Number(data.clearedUpTo ?? 0),
    readVersions: Array.isArray(data.readVersions) ? data.readVersions.map(String) : [],
    clearedVersions: Array.isArray(data.clearedVersions) ? data.clearedVersions.map(String) : [],
});

// The rules only allow reading published notes, so every query must filter on it.
const publishedNotesQuery = (max?: number) => {
    const base = [where("published", "==", true), orderBy("order", "desc")] as const;
    return max
        ? query(collection(firestore, NOTES), ...base, limit(max))
        : query(collection(firestore, NOTES), ...base);
};

export const subscribeRecentReleaseNotes = (onNotes: (notes: ReleaseNote[]) => void, onError: (e: Error) => void) =>
    onSnapshot(
        publishedNotesQuery(RECENT_LIMIT),
        (snapshot) => onNotes(snapshot.docs.map((d) => toReleaseNote(d.data()))),
        onError,
    );

export const fetchAllReleaseNotes = async (): Promise<ReleaseNote[]> => {
    const snapshot = await getDocs(publishedNotesQuery());
    return snapshot.docs.map((d) => toReleaseNote(d.data()));
};

const stateRef = (uid: string) => doc(firestore, STATE, uid);

/** `null` means the user has no saved state yet. */
export const subscribeReleaseNoteState = (
    uid: string,
    onState: (state: ReleaseNoteState | null) => void,
    onError: (e: Error) => void,
) => onSnapshot(stateRef(uid), (snapshot) => onState(snapshot.exists() ? toState(snapshot.data()) : null), onError);

/** Creates the state only if it is still missing, so a second tab cannot overwrite what the first one saved. */
export const createReleaseNoteStateIfMissing = (uid: string, baseline: ReleaseNoteState) =>
    runTransaction(firestore, async (tx) => {
        const snapshot = await tx.get(stateRef(uid));
        if (!snapshot.exists()) {
            tx.set(stateRef(uid), baseline);
        }
    });

export const patchReleaseNoteState = (uid: string, patch: Partial<ReleaseNoteState>) =>
    setDoc(stateRef(uid), patch, { merge: true });

export const addReleaseNoteVersion = (uid: string, field: "readVersions" | "clearedVersions", version: string) =>
    setDoc(stateRef(uid), { [field]: arrayUnion(version) }, { merge: true });
